import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import {
  Users, Copy, RefreshCw, Shield, ShieldCheck,
  UserMinus, Check, Crown, UserPlus, Power, Mail
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function RoleBadge({ role }) {
  if (role === "lead") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
        <Crown size={10} /> Admin
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
      <Shield size={10} /> Agent
    </span>
  );
}

export default function Team() {
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [teamName, setTeamName] = useState("");
  const [creating, setCreating] = useState(false);

  const isLead = user?.team_role === "lead";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/teams/me`);
      setTeam(data.team);
      if (data.team) {
        const membersRes = await axios.get(`${API}/api/teams/members`);
        setMembers(membersRes.data);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const copyCode = () => {
    if (team?.join_code) {
      navigator.clipboard.writeText(team.join_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const regenerateCode = async () => {
    setRegenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/teams/regenerate-code`);
      setTeam((t) => ({ ...t, join_code: data.join_code }));
    } catch (e) {
      alert("Failed to regenerate code");
    }
    setRegenerating(false);
  };

  const changeRole = async (memberId, newRole) => {
    try {
      await axios.put(`${API}/api/teams/members/${memberId}/role`, { role: newRole });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to update role");
    }
  };

  const toggleDisabled = async (memberId, currentlyDisabled) => {
    try {
      await axios.put(`${API}/api/teams/members/${memberId}/status`, { disabled: !currentlyDisabled });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to update status");
    }
  };

  const removeMember = async (memberId, memberName) => {
    if (!window.confirm(`Remove ${memberName} from the team? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/teams/members/${memberId}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to remove member");
    }
  };

  const createTeam = async () => {
    if (!teamName.trim()) return;
    setCreating(true);
    try {
      await axios.post(`${API}/api/teams`, { name: teamName.trim() });
      setShowCreate(false);
      setTeamName("");
      window.location.reload();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to create team");
    }
    setCreating(false);
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="flex justify-center py-16">
          <div className="w-6 h-6 border-2 border-[#FF5C35] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // No team yet — show create option only (join is handled during registration)
  if (!team) {
    return (
      <div className="p-4 md:p-8 max-w-lg">
        <div className="mb-6">
          <h1 className="text-xl md:text-2xl font-semibold font-heading text-slate-900">Team</h1>
          <p className="text-slate-500 text-sm mt-0.5">Create a team to collaborate with your agents</p>
        </div>

        <div className="space-y-4">
          {!showCreate ? (
            <button onClick={() => setShowCreate(true)} className="card p-5 w-full text-left hover:shadow-md transition-all group">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#FFF0EB] flex items-center justify-center">
                  <Users size={18} className="text-[#FF5C35]" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900 group-hover:text-[#FF5C35] transition-colors">Create a Team</p>
                  <p className="text-xs text-slate-500 mt-0.5">Start a new team and invite your agents with a join code</p>
                </div>
              </div>
            </button>
          ) : (
            <div className="card p-5">
              <h3 className="font-semibold text-slate-900 mb-4">Create Your Team</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Team Name</label>
                  <input
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    className="input"
                    placeholder="e.g. Petrosian Team"
                    autoFocus
                    data-testid="team-name-input"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setShowCreate(false)} className="btn-secondary">Cancel</button>
                  <button onClick={createTeam} disabled={creating || !teamName.trim()} className="btn-primary" data-testid="create-team-btn">
                    {creating ? "Creating..." : "Create Team"}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
            <UserPlus size={15} className="text-slate-400 mt-0.5 shrink-0" />
            <p className="text-xs text-slate-500">
              Looking to join an existing team? Ask your team admin for a join code and enter it when you create your account.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Has a team ─────────────────────────────────────────────────────────────
  return (
    <div className="p-4 md:p-8 max-w-2xl">
      <div className="mb-6">
        <h1 className="text-xl md:text-2xl font-semibold font-heading text-slate-900">{team.name}</h1>
        <p className="text-slate-500 text-sm mt-0.5">
          {members.length} member{members.length !== 1 ? "s" : ""} · You are {isLead ? "an Admin" : "an Agent"}
        </p>
      </div>

      <div className="space-y-5">
        {/* Join Code Card — Lead only */}
        {isLead && team.join_code && (
          <div className="card p-5">
            <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-slate-100">
              <div className="w-8 h-8 rounded-lg bg-[#FFF0EB] flex items-center justify-center">
                <UserPlus size={16} className="text-[#FF5C35]" />
              </div>
              <h3 className="font-semibold font-heading text-slate-900">Invite Code</h3>
            </div>
            <p className="text-sm text-slate-600 mb-3">Share this code with agents — they enter it during account registration to join your team.</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-center text-2xl font-bold font-mono tracking-[0.3em] py-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-900" data-testid="team-join-code">
                {team.join_code}
              </code>
              <div className="flex flex-col gap-1.5">
                <button onClick={copyCode} className="btn-secondary py-2 px-3 text-xs" data-testid="copy-join-code">
                  {copied ? <><Check size={12} className="text-green-600" /> Copied</> : <><Copy size={12} /> Copy</>}
                </button>
                <button onClick={regenerateCode} disabled={regenerating} className="btn-secondary py-2 px-3 text-xs" data-testid="regenerate-code">
                  <RefreshCw size={12} className={regenerating ? "animate-spin" : ""} />
                  {regenerating ? "..." : "New"}
                </button>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-2">Regenerating invalidates the old code. Existing members are unaffected.</p>
          </div>
        )}

        {/* Members List */}
        <div className="card p-5">
          <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-slate-100">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
              <Users size={16} className="text-indigo-600" />
            </div>
            <h3 className="font-semibold font-heading text-slate-900">
              {isLead ? "Manage Team Members" : "Team Directory"}
            </h3>
          </div>
          <div className="space-y-1" data-testid="team-members-list">
            {members.map((m) => (
              <div
                key={m.id}
                className={`flex items-center justify-between py-3 px-3 rounded-lg hover:bg-slate-50 transition-colors ${m.disabled ? "opacity-50" : ""}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold text-white shrink-0"
                    style={{ backgroundColor: m.disabled ? "#cbd5e1" : m.id === user.id ? "#FF5C35" : m.team_role === "lead" ? "#d97706" : "#94a3b8" }}>
                    {(m.name || m.email || "?")[0].toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-slate-900 text-sm truncate">
                        {m.name || "Unnamed"}
                        {m.id === user.id && <span className="text-xs text-slate-400 ml-1">(you)</span>}
                      </p>
                      <RoleBadge role={m.team_role} />
                      {m.disabled && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">
                          Disabled
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate flex items-center gap-1">
                      <Mail size={10} /> {m.email}
                    </p>
                  </div>
                </div>

                {/* Admin controls — only for leads, and not for themselves */}
                {isLead && m.id !== user.id && (
                  <div className="flex items-center gap-1.5 shrink-0 ml-3">
                    {/* Enable/Disable toggle */}
                    <button
                      onClick={() => toggleDisabled(m.id, m.disabled)}
                      className={`btn-secondary py-1.5 px-2.5 text-xs ${m.disabled ? "text-green-600 hover:bg-green-50" : "text-amber-600 hover:bg-amber-50"}`}
                      title={m.disabled ? "Enable account" : "Disable account"}
                    >
                      <Power size={12} />
                      {m.disabled ? "Enable" : "Disable"}
                    </button>

                    {/* Role change */}
                    {m.team_role === "member" ? (
                      <button
                        onClick={() => changeRole(m.id, "lead")}
                        className="btn-secondary py-1.5 px-2.5 text-xs"
                        title="Promote to Admin"
                      >
                        <ShieldCheck size={12} /> Promote
                      </button>
                    ) : (
                      <button
                        onClick={() => changeRole(m.id, "member")}
                        className="btn-secondary py-1.5 px-2.5 text-xs"
                        title="Demote to Agent"
                      >
                        <Shield size={12} /> Demote
                      </button>
                    )}

                    {/* Remove */}
                    <button
                      onClick={() => removeMember(m.id, m.name || m.email)}
                      className="btn-secondary py-1.5 px-2 text-xs text-red-500 hover:text-red-600 hover:bg-red-50"
                      title="Remove from team"
                    >
                      <UserMinus size={12} />
                    </button>
                  </div>
                )}
              </div>
            ))}
            {members.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-4">No members yet. Share your join code to invite agents.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
